"use client";

import { Link } from "@/i18n/navigation";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useExtraProp } from "@/contexts/ExtraPropContext";

// Progress bar for non-logged users (uses window scroll, tracks article element)
const WindowProgressBar = () => {
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        const updateProgress = () => {
            const article = document.getElementById("article-content") || document.getElementById("term-content");
            if (!article) {
                setScrollProgress(0);
                return;
            }

            const articleTop = article.offsetTop;
            const articleHeight = article.offsetHeight;
            const viewportHeight = window.innerHeight;
            const scrollTop = window.scrollY;

            const headerOffset = 100;
            const startPoint = articleTop - headerOffset;
            const endPoint = articleTop + articleHeight - viewportHeight;

            const scrollableDistance = endPoint - startPoint;
            if (scrollableDistance <= 0) {
                setScrollProgress(100);
                return;
            }

            const scrolled = scrollTop - startPoint;
            const percentage = (scrolled / scrollableDistance) * 100;
            setScrollProgress(Math.max(0, Math.min(percentage, 100)));
        };

        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    updateProgress();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        updateProgress();

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <div className="w-full h-[3px] bg-gray-100 dark:bg-grayDark-800 rounded-full mb-4 overflow-hidden">
            <div
                className="h-full bg-primary rounded-full transition-all duration-150 ease-out"
                style={{ width: `${scrollProgress}%` }}
            />
        </div>
    );
};

// Progress bar for logged users (uses extraProp container scroll)
const ExtraPropProgressBar = () => {
    const [scrollProgress, setScrollProgress] = useState(0);
    const extraProp = useExtraProp();

    useEffect(() => {
        if (!extraProp?.current) return;

        const container = extraProp.current;

        const updateProgress = () => {
            const article = document.getElementById("article-content") || document.getElementById("term-content");
            if (!article) {
                setScrollProgress(0);
                return;
            }

            const scrollTop = container.scrollTop;
            const articleTop = article.offsetTop;
            const articleHeight = article.offsetHeight;
            const containerHeight = container.clientHeight;

            const headerOffset = 80;
            const startPoint = articleTop - headerOffset;
            const endPoint = articleTop + articleHeight - containerHeight;

            const scrollableDistance = endPoint - startPoint;
            if (scrollableDistance <= 0) {
                setScrollProgress(100);
                return;
            }

            const scrolled = scrollTop - startPoint;
            const percentage = (scrolled / scrollableDistance) * 100;
            setScrollProgress(Math.max(0, Math.min(percentage, 100)));
        };

        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    updateProgress();
                    ticking = false;
                });
                ticking = true;
            }
        };

        container.addEventListener("scroll", handleScroll, { passive: true });
        updateProgress();

        return () => container.removeEventListener("scroll", handleScroll);
    }, [extraProp]);

    return (
        <div className="w-full h-[3px] bg-gray-100 dark:bg-grayDark-800 rounded-full mb-4 overflow-hidden">
            <div
                className="h-full bg-primary rounded-full transition-all duration-150 ease-out"
                style={{ width: `${scrollProgress}%` }}
            />
        </div>
    );
};

// Composant TableOfContents
const TableOfContents = ({
    headings,
    forMobiles = false,
    termRef = null,
    containerRef = null,
}) => {
    const [activeId, setActiveId] = useState("");
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const extraProp = useExtraProp();
    const tocListRef = useRef(null);
    const isClickScrolling = useRef(false);
    const hasScrolledToHash = useRef(false);

    // Detect if user is logged in
    useEffect(() => {
        setIsLoggedIn(!!extraProp?.current);
    }, [extraProp]);

    // Simple scroll function - works for both logged and non-logged users
    const scrollToHeading = useCallback((id) => {
        const element = document.getElementById(id);
        if (!element) return;

        const headerOffset = extraProp?.current ? 80 : 100;

        if (extraProp?.current) {
            // Logged user: scroll the extraProp container
            const container = extraProp.current;
            const elementRect = element.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const scrollTop = container.scrollTop;

            const targetPosition = scrollTop + elementRect.top - containerRect.top - headerOffset;

            container.scrollTo({
                top: Math.max(0, targetPosition),
                behavior: "smooth",
            });
        } else {
            // Non-logged user: scroll the window
            const elementRect = element.getBoundingClientRect();
            const targetPosition = window.scrollY + elementRect.top - headerOffset;

            window.scrollTo({
                top: Math.max(0, targetPosition),
                behavior: "smooth",
            });
        }
    }, [extraProp]);

    // Handle click on TOC item
    const handleClick = useCallback((e, id) => {
        e.preventDefault();

        // Mark as click-scrolling to prevent observer updates
        isClickScrolling.current = true;
        setActiveId(id);

        // Scroll to the heading
        scrollToHeading(id);

        // Update URL hash
        window.history.replaceState(null, "", `#${id}`);

        // Re-enable observer after scroll completes
        setTimeout(() => {
            isClickScrolling.current = false;
        }, 1000);
    }, [scrollToHeading]);

    // Scroll to anchor on page load if hash exists (only once)
    useEffect(() => {
        if (typeof window === "undefined" || hasScrolledToHash.current) return;

        const hash = window.location.hash;
        if (hash) {
            hasScrolledToHash.current = true;
            const id = hash.replace("#", "");
            // Wait for page to be ready
            setTimeout(() => {
                scrollToHeading(id);
                setActiveId(id);
            }, 300);
        }
    }, [scrollToHeading]);

    // Auto-scroll TOC to keep active item visible (only when not click-scrolling)
    useEffect(() => {
        if (forMobiles || !activeId || isClickScrolling.current) return;

        const activeElement = document.querySelector(`[data-heading-id="${activeId}"]`);
        if (activeElement) {
            // scrollIntoView with block: 'nearest' only scrolls the nearest scrollable ancestor
            // and won't affect the main page scroll
            activeElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [activeId, forMobiles]);

    // Intersection Observer for active heading detection
    useEffect(() => {
        if (forMobiles) return;

        const observerOptions = {
            root: null,
            rootMargin: "-80px 0px -50% 0px",
            threshold: 0,
        };

        const callback = (entries) => {
            // Don't update if user is click-scrolling
            if (isClickScrolling.current) return;

            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveId(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(callback, observerOptions);
        const elements = headings
            .map((heading) => document.getElementById(heading.id))
            .filter(Boolean);

        elements.forEach((element) => observer.observe(element));

        return () => {
            elements.forEach((element) => observer.unobserve(element));
        };
    }, [headings, forMobiles]);

    // Determine which progress bar to show
    const showExtraPropProgress = isLoggedIn && !forMobiles;
    const showWindowProgress = !isLoggedIn && !forMobiles;

    // For mobile, simplified render
    if (forMobiles) {
        return (
            <nav>
                <ul className="space-y-1">
                    {headings.map((heading) => (
                        <li
                            key={`mobile-${heading.id}`}
                            style={{ paddingLeft: heading.level > 2 ? `${(heading.level - 2) * 12}px` : 0 }}
                        >
                            <a
                                href={`#${heading.id}`}
                                onClick={(e) => handleClick(e, heading.id)}
                                className="block py-1 text-[13px] text-gray-600 dark:text-grayDark-400 hover:text-gray-900 dark:hover:text-grayDark-200 transition-colors"
                            >
                                {heading.text}
                            </a>
                        </li>
                    ))}
                </ul>
            </nav>
        );
    }

    return (
        <nav className="flex flex-col h-full min-h-0">
            {showExtraPropProgress && <ExtraPropProgressBar />}
            {showWindowProgress && <WindowProgressBar />}

            <ul
                ref={tocListRef}
                className="flex-1 min-h-0 overflow-y-auto pr-1 scrollbar-thin"
            >
                {headings.map((heading, index) => {
                    const isActive = activeId === heading.id;
                    const isH2 = heading.level === 2;
                    const depth = heading.level - 2; // 0 for h2, 1 for h3, 2 for h4...

                    return (
                        <li
                            key={heading.id}
                            className={`relative ${isH2 && index > 0 ? "mt-3" : ""}`}
                        >
                            {/* Tree lines for sub-headings */}
                            {depth > 0 && (
                                <div className="absolute left-0 top-0 bottom-0 flex" style={{ width: `${depth * 16}px` }}>
                                    {Array.from({ length: depth }).map((_, i) => (
                                        <div
                                            key={i}
                                            className="w-4 h-full flex justify-center"
                                        >
                                            <div className="w-px h-full bg-gray-200 dark:bg-grayDark-700" />
                                        </div>
                                    ))}
                                </div>
                            )}
                            <a
                                href={`#${heading.id}`}
                                onClick={(e) => handleClick(e, heading.id)}
                                data-heading-id={heading.id}
                                style={{ paddingLeft: depth > 0 ? `${depth * 16}px` : 0 }}
                                className={`
                                    relative flex items-center py-1.5 text-[13px] leading-snug
                                    transition-all duration-150
                                    ${isActive
                                        ? "text-gray-900 dark:text-white"
                                        : "text-gray-500 dark:text-grayDark-400 hover:text-gray-800 dark:hover:text-grayDark-200"
                                    }
                                    ${isH2 ? "font-medium" : ""}
                                `}
                            >
                                {/* Connector dot/line for sub-headings */}
                                {depth > 0 && (
                                    <span className={`
                                        absolute flex items-center
                                        ${isActive ? "text-primary" : "text-gray-300 dark:text-grayDark-600"}
                                    `} style={{ left: `${(depth - 1) * 16 + 6}px` }}>
                                        <span className="w-2 h-px bg-current" />
                                        <span className={`w-1.5 h-1.5 rounded-full border-[1.5px] border-current ${isActive ? "bg-primary" : "bg-white dark:bg-grayDark-900"}`} />
                                    </span>
                                )}
                                {/* H2 indicator */}
                                {isH2 && (
                                    <span className={`
                                        shrink-0 w-1.5 h-1.5 rounded-full mr-2 transition-colors
                                        ${isActive ? "bg-primary" : "bg-gray-300 dark:bg-grayDark-600"}
                                    `} />
                                )}
                                <span className={depth > 0 ? "ml-4" : ""}>{heading.text}</span>
                            </a>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
};

export default TableOfContents;
